// ============================================================================
// PROPERTY SERVICE
// ============================================================================
// Business logic and API calls for Property Master module

import { get, post, put, del } from '../client/axiosClient';
import { propertyEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  Property,
  CreatePropertyRequest,
  UpdatePropertyRequest,
  PropertySearchRequest,
} from '../types';

/**
 * Property Master Service Class
 */
class PropertyService {
  /**
   * Get all properties (paginated)
   */
  async getAll(params?: PropertySearchRequest): Promise<PaginatedResponse<Property>> {
    const endpoint = params
      ? buildEndpointWithQuery('/Property', params)
      : propertyEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /**
   * Get property by ID
   */
  async getById(id: ID): Promise<ApiResponse<Property>> {
    const response = await get<ApiResponse<Property>>(propertyEndpoints.getById(id));
    return response.data;
  }

  /**
   * Get property by code
   */
  async getByCode(code: string): Promise<ApiResponse<Property>> {
    const response = await get<ApiResponse<Property>>(propertyEndpoints.getByCode(code));
    return response.data;
  }

  /**
   * Create new property
   */
  async create(data: CreatePropertyRequest): Promise<ApiResponse<Property>> {
    const response = await post<ApiResponse<Property>>(
      propertyEndpoints.create(),
      data
    );
    return response.data;
  }

  /**
   * Update property
   */
  async update(id: ID, data: UpdatePropertyRequest): Promise<ApiResponse<Property>> {
    const response = await put<ApiResponse<Property>>(
      propertyEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete property
   */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(propertyEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }

  /**
   * Validate property code uniqueness
   */
  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = propertyEndpoints.validateCode(code);
    if (excludeId) {
      endpoint = `${endpoint}?excludeId=${excludeId}`;
    }
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }

  /**
   * Validate property name uniqueness
   */
  async validateName(name: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = propertyEndpoints.validateName(encodeURIComponent(name));
    if (excludeId) {
      endpoint = `${endpoint}?excludeId=${excludeId}`;
    }
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const propertyService = new PropertyService();
export default propertyService;
